import { api } from '@/lib/api/axios';
import type { User } from '@/types';

export interface NotificationTopicPreference {
  topicKey: string;
  name: string;
  description: string;
  inAppEnabled: boolean;
  whatsappEnabled: boolean;
}

export interface NotificationPreferences {
  pushEnabled: boolean;
  topics: NotificationTopicPreference[];
}

export interface NotificationPreferencesUpdate {
  pushEnabled?: boolean;
  topicKey?: string;
  inAppEnabled?: boolean;
  whatsappEnabled?: boolean;
}

export const notificationPreferencesQueryKey = (companyId?: string) =>
  ['users', 'me', 'notification-preferences', companyId] as const;

export const UsersService = {
  list: (filters?: { socialNumber?: string }) => api.get<User[]>('/users', { params: filters }),
  get: (id: string) => api.get(`/users/${id}`),
  companies: () => api.get(`/users/companies`),
  updateMe: (data: { name: string; email: string | null; birthDate: string | null }) =>
    api.patch<User>('/users/me', data),
  getNotificationPreferences: async () => {
    const { data } = await api.get<NotificationPreferences>('/users/me/notification-preferences');
    return data;
  },
  updateNotificationPreferences: async (preferences: NotificationPreferencesUpdate) => {
    const { data } = await api.patch<NotificationPreferences>('/users/me/notification-preferences', preferences);
    return data;
  },
  uploadMyPhoto: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post<User>('/users/me/profile-photo', formData);
  },
  requestPhoneChangeCode: (data: { phoneNumber: string; method: 'whatsapp' | 'sms' }) =>
    api.post<{ phoneNumber: string; expiresAt: string; method: 'whatsapp' | 'sms' }>(
      '/users/me/phone-change/request-code',
      data,
    ),
  confirmPhoneChange: (data: { phoneNumber: string; code: string }) =>
    api.post<{ phoneNumber: string; isPhoneVerified: boolean; isPhoneWhatsapp: boolean }>(
      '/users/me/phone-change/confirm',
      data,
    ),
  findBySocialNumber: async (socialNumber: string): Promise<User | null> => {
    const response = await api.get<User>(`/users/by-social-number/${socialNumber}`, {
      validateStatus: (status) => (status >= 200 && status < 300) || status === 404,
    });

    if (response.status === 404) {
      return null;
    }

    const { data } = response;

    return data;
  },
};
